<template>
  <el-dialog
    v-model="visible"
    title="新增班级"
    width="600px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form :model="form" label-width="100px" :rules="rules" ref="formRef">
      <el-form-item label="班级编号" prop="classCode">
        <el-input v-model="form.classCode" disabled placeholder="系统自动生成" />
      </el-form-item>
      <el-form-item label="班级名称" prop="className">
        <el-input v-model="form.className" placeholder="请输入班级名称" />
      </el-form-item>
      <el-form-item label="所属学院" prop="collegeCode">
        <el-select v-model="form.collegeCode" placeholder="请选择学院" @change="handleCollegeChange" style="width: 100%">
          <el-option v-for="item in collegeList" :key="item.collegeCode" :label="item.collegeName" :value="item.collegeCode" />
        </el-select>
      </el-form-item>
      <el-form-item label="专业名称" prop="majorCode">
        <el-select v-model="form.majorCode" placeholder="请选择专业" @change="handleMajorChange" style="width: 100%" :disabled="!form.collegeCode">
          <el-option v-for="item in filteredMajorList" :key="item.majorCode" :label="item.majorName" :value="item.majorCode" />
        </el-select>
      </el-form-item>
      <el-form-item label="导员姓名" prop="counselorName">
        <el-select v-model="form.counselorName" placeholder="请选择导员" @change="handleCounselorChange" style="width: 100%">
          <el-option v-for="item in filteredCounselorList" :key="item.counselorName" :label="item.counselorName" :value="item.counselorName" />
        </el-select>
      </el-form-item>
      <el-form-item label="导员电话" prop="counselorPhone">
        <el-input v-model="form.counselorPhone" disabled placeholder="选择导员后自动填充" />
      </el-form-item>
      <el-form-item label="班级人数" prop="studentCount">
        <el-input-number v-model="form.studentCount" :min="1" :max="100" style="width: 100%" />
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue'
import request from '@/utils/request'

export default {
  name: 'AddClassDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    existingClasses: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'success'],
  setup(props, { emit }) {
    const visible = computed({
      get: () => props.modelValue,
      set: (val) => emit('update:modelValue', val)
    })

    const formRef = ref(null)
    const saving = ref(false)

    const form = reactive({
      classCode: '',
      className: '',
      collegeCode: '',
      collegeName: '',
      majorCode: '',
      majorName: '',
      counselorName: '',
      counselorPhone: '',
      studentCount: 15
    })

    const rules = {
      className: [{ required: true, message: '请输入班级名称', trigger: 'blur' }],
      collegeCode: [{ required: true, message: '请选择学院', trigger: 'change' }],
      majorCode: [{ required: true, message: '请选择专业', trigger: 'change' }],
      counselorName: [{ required: true, message: '请选择导员', trigger: 'change' }]
    }

    // 数据列表
    const collegeList = ref([])
    const majorList = ref([])
    const counselorList = ref([])

    // 根据学院筛选专业
    const filteredMajorList = computed(() => {
      if (!form.collegeCode) return []
      return majorList.value.filter(m => m.collegeCode === form.collegeCode)
    })

    // 根据学院筛选导员
    const filteredCounselorList = computed(() => {
      if (!form.collegeCode) return []
      return counselorList.value.filter(c => c.collegeCode === form.collegeCode)
    })

    // 生成班级编号
    const generateClassCode = () => {
      if (!form.majorCode) return ''
      
      // 查找该专业下已有的班级数量
      const existingClasses = props.existingClasses.filter(item => item.majorCode === form.majorCode)
      const classNum = existingClasses.length + 1
      
      // 班级编号格式：专业代码(8位) + 班级序号(2位) + 01
      return `${form.majorCode}${String(classNum).padStart(2, '0')}01`
    }

    // 监听专业变化，自动生成班级编号和匹配导员
    watch(() => form.majorCode, (newVal) => {
      if (newVal) {
        form.classCode = generateClassCode()
        // 根据专业查找对应的导员
        const counselor = counselorList.value.find(c => c.majorCode === newVal)
        if (counselor) {
          form.counselorName = counselor.counselorName
          form.counselorPhone = counselor.counselorPhone
        }
      } else {
        form.classCode = ''
        form.counselorName = ''
        form.counselorPhone = ''
      }
    })

    // 学院选择变化
    const handleCollegeChange = (collegeCode) => {
      const college = collegeList.value.find(c => c.collegeCode === collegeCode)
      if (college) {
        form.collegeName = college.collegeName
      }
      form.majorCode = ''
      form.majorName = ''
      form.counselorName = ''
      form.counselorPhone = ''
      form.classCode = ''
    }

    // 专业选择变化
    const handleMajorChange = (majorCode) => {
      const major = filteredMajorList.value.find(m => m.majorCode === majorCode)
      if (major) {
        form.majorName = major.majorName
      }
    }

    // 导员选择变化
    const handleCounselorChange = (counselorName) => {
      const counselor = filteredCounselorList.value.find(c => c.counselorName === counselorName)
      if (counselor) {
        form.counselorPhone = counselor.counselorPhone
      }
    }

    // 获取学院列表
    const fetchCollegeList = async () => {
      try {
        const response = await request.get('/colleges/list')
        if (response.data && response.data.code === 200) {
          collegeList.value = response.data.data || []
        }
      } catch (error) {
        console.error('获取学院列表失败:', error)
      }
    }

    // 获取专业列表
    const fetchMajorList = async () => {
      try {
        const response = await request.get('/majors/list')
        if (response.data && response.data.code === 200) {
          majorList.value = response.data.data || []
        }
      } catch (error) {
        console.error('获取专业列表失败:', error)
      }
    }

    // 获取导员列表
    const fetchCounselorList = async () => {
      try {
        const response = await request.get('/counselors/list')
        if (response.data && response.data.code === 200) {
          counselorList.value = response.data.data || []
        }
      } catch (error) {
        console.error('获取导员列表失败:', error)
      }
    }

    // 打开弹窗时加载数据
    watch(() => props.modelValue, (newVal) => {
      if (newVal) {
        resetForm()
        fetchCollegeList()
        fetchMajorList()
        fetchCounselorList()
      }
    })

    // 重置表单
    const resetForm = () => {
      form.classCode = ''
      form.className = ''
      form.collegeCode = ''
      form.collegeName = ''
      form.majorCode = ''
      form.majorName = ''
      form.counselorName = ''
      form.counselorPhone = ''
      form.studentCount = 15
      
      if (formRef.value) {
        formRef.value.resetFields()
      }
    }

    // 关闭弹窗
    const handleClose = () => {
      visible.value = false
    }

    // 保存班级
    const handleSave = async () => {
      if (!formRef.value) return
      
      await formRef.value.validate(async (valid) => {
        if (valid) {
          saving.value = true
          try {
            const response = await request.post('/class/add', form)
            if (response.data && response.data.code === 200) {
              handleClose()
              emit('success')
            } else {
              alert(response.data.msg || '保存失败')
            }
          } catch (error) {
            console.error('保存班级失败:', error)
            alert('保存失败，请检查网络连接')
          } finally {
            saving.value = false
          }
        }
      })
    }

    return {
      visible,
      formRef,
      form,
      rules,
      saving,
      collegeList,
      filteredMajorList,
      filteredCounselorList,
      handleCollegeChange,
      handleMajorChange,
      handleCounselorChange,
      handleClose,
      handleSave
    }
  }
}
</script>

<style scoped src="./AddClassDialog.css"></style>
